import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import type { Bill } from "@luckymenu/billing-core";
import { DEFAULT_TIME_ZONE, uuidv7 } from "@luckymenu/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type CustomerDraft, customerFromDraft, EMPTY_CUSTOMER } from "../billing/customer";
import type { BillDiscount, BillDiscounts } from "../billing/discounts";
import { type BillingError, billingProblemOf } from "../billing/problems";
import { quickBillCalculation } from "../billing/quick";
import { type DraftTarget, draftToLines } from "../ordering/draft";
import { usePlatform } from "../provider";
import { useBillingSettings } from "./use-bill";
import { useCurrentMembership } from "./use-current";
import { useNow } from "./use-live-board";
import { useMenu } from "./use-menu";
import { useCan } from "./use-navigation";
import { useOrderDraft, useRestaurantId } from "./use-order";

/** The quick bill's items: a draft like any other, kept on the device. */
export const QUICK_TARGET: DraftTarget = { kind: "quick" };

/** The takeaway behind a quick bill, kept on the device until the next one starts. */
type QuickSession = {
	orderId: string;
	/** The batch's request ID: a retry of the same quick bill creates one order. */
	requestId: string;
	/** The server has the order: its lines left the draft. */
	created: boolean;
};

/**
 * Quick bill for the counter (OP-13, P4-T19): pick items (`usePicker` with
 * `QUICK_TARGET`), see the bill as it will be (billing-core over the
 * menu's prices with the restaurant's settings), optionally a bill
 * discount, then `issue()` creates a takeaway order with those items behind
 * the scenes and issues its bill. Then payment, as on any bill
 * (`usePayment(orderId)`), and `reset()` for the next customer. The order
 * and its batch carry IDs made once per quick bill, so a retry after a
 * lost answer makes one order; the session is kept on the device, so a
 * reload comes back to the bill being paid.
 *
 * `stage`:
 * - `items`: picking; `calculation` is the preview.
 * - `billing`: the order and its bill on their way.
 * - `review`: the order exists but its bill was refused (`problem`, such as
 *   prices that changed meanwhile): show that order's bill screen
 *   (`useBillScreen(orderId, { initialDiscounts })`) to check and bill it.
 * - `payment`: billed, something left to pay.
 * - `done`: paid in full.
 */
export function useQuickBill() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const { storage } = usePlatform();
	const restaurantId = useRestaurantId();
	const storageKey = `luckymenu.quickBill.${restaurantId ?? "none"}`;
	const draft = useOrderDraft(QUICK_TARGET);
	const menu = useMenu();
	const { settings } = useBillingSettings();
	const canOrder = useCan("order.create");
	const canBill = useCan("bill.create");
	const canDiscount = useCan("bill.discount");
	const [session, setSessionState] = useState<QuickSession | null>(null);
	const [loadedKey, setLoadedKey] = useState<string | null>(null);
	const [billDiscount, setBillDiscountState] = useState<BillDiscount | null>(null);
	const [customer, setCustomerDraft] = useState<CustomerDraft>(EMPTY_CUSTOMER);
	const [problem, setProblem] = useState<BillingError | null>(null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		let cancelled = false;
		storage.getItem(storageKey).then(
			(raw) => {
				if (cancelled) return;
				try {
					setSessionState(raw ? (JSON.parse(raw) as QuickSession) : null);
				} catch {
					setSessionState(null);
				}
				setLoadedKey(storageKey);
			},
			() => {
				if (!cancelled) setLoadedKey(storageKey);
			},
		);
		return () => {
			cancelled = true;
		};
	}, [storage, storageKey]);
	const setSession = useCallback(
		(next: QuickSession | null) => {
			setSessionState(next);
			if (next) void storage.setItem(storageKey, JSON.stringify(next)).catch(() => {});
			else void storage.removeItem(storageKey).catch(() => {});
		},
		[storage, storageKey],
	);

	const bill = useQuery({
		...trpc.staff.billing.getBill.queryOptions({ orderId: session?.orderId ?? "" }),
		enabled: Boolean(session?.created) && canBill,
	});
	const createOrder = useMutation(trpc.staff.ordering.createOrder.mutationOptions());
	const finalize = useMutation(trpc.staff.billing.finalize.mutationOptions());

	// Offers apply as they run now, in the restaurant's time zone (P9-T04).
	const timeZone = useCurrentMembership().data?.restaurant.timezone ?? DEFAULT_TIME_ZONE;
	const now = useNow(60_000);
	const calculation = useMemo(
		() =>
			settings
				? quickBillCalculation(draft.draft.lines, menu.data ?? null, settings, billDiscount, {
						at: new Date(now),
						timeZone,
					})
				: null,
		[settings, draft.draft.lines, menu.data, billDiscount, now, timeZone],
	);

	const customerCheck = customerFromDraft(customer);

	const stage =
		loadedKey !== storageKey
			? ("loading" as const)
			: busy
				? ("billing" as const)
				: !session?.created
					? ("items" as const)
					: bill.data
						? bill.data.status === "final" && bill.data.balancePaise === 0
							? ("done" as const)
							: ("payment" as const)
						: bill.isError
							? ("review" as const)
							: ("loading" as const);

	const issue = useCallback(async (): Promise<Bill | null> => {
		if (!calculation || calculation.lines.length === 0 || busy) return null;
		if (!customerCheck.ok) return null;
		setProblem(null);
		setBusy(true);
		// Taken now: the draft empties once the order has its lines.
		const expectedTotalPaise = calculation.totals.totalPaise;
		const current = session ?? { orderId: uuidv7(), requestId: uuidv7(), created: false };
		try {
			if (!current.created) {
				setSession(current);
				const order = await createOrder.mutateAsync({
					id: current.orderId,
					type: "takeaway",
					// The takeaway's name and phone are the bill's (BL-11, P4-T24).
					customerName: customerCheck.customer.name,
					customerPhone: customerCheck.customer.phone,
					batch: { clientRequestId: current.requestId, lines: draftToLines(draft.draft.lines) },
				});
				queryClient.setQueryData(
					trpc.staff.ordering.getOrder.queryKey({ orderId: order.id }),
					order,
				);
				draft.clear();
				setSession({ ...current, created: true });
			}
			const issued = await finalize.mutateAsync({
				orderId: current.orderId,
				billDiscount,
				...(customerCheck.customer.gstin
					? { customer: { gstin: customerCheck.customer.gstin } }
					: {}),
				expectedTotalPaise,
			});
			queryClient.setQueryData(
				trpc.staff.billing.getBill.queryKey({ orderId: issued.orderId }),
				issued,
			);
			queryClient.setQueryData(trpc.staff.billing.getBill.queryKey({ billId: issued.id }), issued);
			return issued;
		} catch (error) {
			setProblem(billingProblemOf(error));
			return null;
		} finally {
			setBusy(false);
			void queryClient.invalidateQueries({ queryKey: trpc.staff.ordering.pathKey() });
			void queryClient.invalidateQueries({ queryKey: trpc.staff.billing.listBills.pathKey() });
			void queryClient.invalidateQueries({
				queryKey: trpc.staff.billing.getBill.queryKey({ orderId: current.orderId }),
			});
		}
	}, [
		calculation,
		busy,
		customerCheck,
		draft,
		session,
		setSession,
		createOrder,
		finalize,
		billDiscount,
		queryClient,
		trpc,
	]);

	const reset = useCallback(() => {
		setSession(null);
		setBillDiscountState(null);
		setCustomerDraft(EMPTY_CUSTOMER);
		setProblem(null);
	}, [setSession]);

	const initialDiscounts: BillDiscounts = useMemo(
		() => ({ bill: billDiscount, items: {} }),
		[billDiscount],
	);

	return {
		stage,
		/** Picking and billing need `order.create` and `bill.create`. */
		allowed: canOrder && canBill,
		canDiscount,
		/** The picked items: `useOrderDraft(QUICK_TARGET)`. */
		draft,
		/** The bill as it will be, or null until the settings are in. */
		calculation,
		billDiscount,
		setBillDiscount: useCallback((discount: BillDiscount | null) => {
			setProblem(null);
			setBillDiscountState(discount);
		}, []),
		/** Optional name, phone and GSTIN for the bill (P4-T24). */
		customer,
		customerErrors: customerCheck.ok ? {} : customerCheck.errors,
		setCustomer: useCallback((field: keyof CustomerDraft, value: string) => {
			setProblem(null);
			setCustomerDraft((c) => ({ ...c, [field]: value }));
		}, []),
		/** The order behind the bill, once it exists. */
		orderId: session?.created ? session.orderId : null,
		/** For the `review` stage's bill screen. */
		initialDiscounts,
		bill: bill.data ?? null,
		issue,
		busy,
		problem,
		clearProblem: useCallback(() => setProblem(null), []),
		/** Starts the next quick bill. */
		reset,
	};
}
